import React, { useMemo, useState } from 'react';
import './index.scss';
import {
    RS_DATA,
    SCORES,
    FAVORABLE_MIN,
    SORTS,
    sortRows,
    agreementForSpread,
    meanToPct,
    HEADER_TEXT,
} from './constants';
import SectionHeader from '../../../../../SectionHeader';

const RatingsSpread = (props) => {
    let distributionStripData = props.fetchDistributionStripApi.data && props.fetchDistributionStripApi.data.data ? props.fetchDistributionStripApi.data.data : []
    const [sort, setSort] = useState('favorable');

    const rows = useMemo(() => sortRows(distributionStripData, sort), [sort]);

    return (
        <>
            <SectionHeader
                title={HEADER_TEXT.title}
                description={HEADER_TEXT.description}
            />
        <div className='cardwrapper'>
            <div className='ratings-spread'>
                {/* Toolbar: explainer on the left, sort toggle on the right */}
                <div className='ratings-spread__toolbar'>
                    <span className='ratings-spread__intro'>
                        Each row shows how stakeholder ratings are actually spread across the 1–6
                        scale for a category. Bars are the share of responses at each score. Markers
                        show <span className='ratings-spread__ink-self'>Self</span> and{' '}
                        <span className='ratings-spread__ink-others'>All But Self</span> means. Two
                        means can match while the underlying distributions disagree — this view
                        surfaces that.
                    </span>
                    <div className='ratings-spread__sort' role='tablist' aria-label='Sort rows'>
                        {SORTS.map((s) => (
                            <button
                                key={s.id}
                                type='button'
                                role='tab'
                                aria-selected={sort === s.id}
                                className={`ratings-spread__sort-btn${sort === s.id ? ' is-active' : ''}`}
                                onClick={() => setSort(s.id)}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Screen-only horizontal scroll on narrow viewports (the grid has a
                    fixed min-width); the wrapper is inert in print (@media screen in scss). */}
                <div className='ratings-spread__scroll'>
                    {/* Column headers */}
                    <div className='ratings-spread__header'>
                        <span className='ratings-spread__h'>Category</span>
                        <span className='ratings-spread__h'>Distribution (1 → 6)</span>
                        <span className='ratings-spread__h ratings-spread__h--right'>Favorable</span>
                        <span className='ratings-spread__h ratings-spread__h--right'>Spread (SD)</span>
                        <span className='ratings-spread__h ratings-spread__h--right'>Agreement</span>
                    </div>

                    {/* One row per category */}
                    <div className='ratings-spread__rows'>
                        {rows.map((row) => {
                        const agreement = agreementForSpread(row.spread);
                        const rowMax = Math.max(...row.dist);

                        return (
                            <div className='ratings-spread__row' key={row.category}>
                                <div className='ratings-spread__category'>
                                    <span className='ratings-spread__category-name'>{row.category}</span>
                                    <span className='ratings-spread__category-meta'>
                                        Self {row.self.toFixed(2)} &middot; Others {row.others.toFixed(2)}
                                    </span>
                                </div>

                                <div className='ratings-spread__dist'>
                                    <div className='ratings-spread__bars'>
                                        {row.dist.map((share, i) => {
                                            const score = i + 1;
                                            const favorable = score >= FAVORABLE_MIN;
                                            const height = rowMax > 0 ? (share / rowMax) * 100 : 0;
                                            return (
                                                <div
                                                    key={score}
                                                    className={`ratings-spread__col${favorable ? ' is-favorable' : ''}`}
                                                >
                                                    <div
                                                        className='ratings-spread__bar'
                                                        style={{ height: `${height}%` }}
                                                    />
                                                </div>
                                            );
                                        })}

                                        {/* Mean markers overlaid on the bars (Self drawn last, on top) */}
                                        <span
                                            className='ratings-spread__mean ratings-spread__mean--others'
                                            style={{ left: `${meanToPct(row.others)}%` }}
                                            title={`All But Self mean ${row.others.toFixed(2)}`}
                                        >
                                            <span className='ratings-spread__mean-line' />
                                            <span className='ratings-spread__mean-dot' />
                                        </span>
                                        <span
                                            className='ratings-spread__mean ratings-spread__mean--self'
                                            style={{ left: `${meanToPct(row.self)}%` }}
                                            title={`Self mean ${row.self.toFixed(2)}`}
                                        >
                                            <span className='ratings-spread__mean-line' />
                                            <span className='ratings-spread__mean-dot' />
                                        </span>
                                    </div>

                                    <div className='ratings-spread__scale'>
                                        {SCORES.map((s) => (
                                            <span key={s}>{s}</span>
                                        ))}
                                    </div>
                                </div>

                                <span className='ratings-spread__favorable'>{row.favorable}%</span>
                                <span className='ratings-spread__spread'>{row.spread.toFixed(2)}</span>
                                <span className={`ratings-spread__badge ratings-spread__badge--${agreement.modifier}`}>
                                    {agreement.label}
                                </span>
                            </div>
                        );
                    })}
                    </div>
                </div>

                {/* Legend */}
                <div className='ratings-spread__legend'>
                    <span className='ratings-spread__legend-item'>
                        <span className='ratings-spread__dot ratings-spread__dot--self' />
                        Self mean
                    </span>
                    <span className='ratings-spread__legend-item'>
                        <span className='ratings-spread__dot ratings-spread__dot--others' />
                        All But Self mean
                    </span>
                    <span className='ratings-spread__legend-item'>
                        <span className='ratings-spread__swatch' />
                        Favorable zone (4+)
                    </span>
                </div>
            </div>
        </div>
        </>
    );
};

export default RatingsSpread;
